import { createFileRoute, Link } from "@tanstack/react-router";
import useRequest from "@/lib/request/useRequest";
import {
    getClassificationLabel,
    Imagery,
    ModelClassification,
} from "@/types/imagery";
import { FieldSummary } from "@/types/field";
import { useEffect, useMemo, useState } from "react";
import {
    Button,
    AutocompleteItem,
    Input,
    Divider,
    ButtonGroup,
    Autocomplete,
    DateRangePicker,
    RangeValue,
    Checkbox,
} from "@heroui/react";
import dayjs from "dayjs";
import {
    ColumnDef,
    RowSelectionState,
    SortingState,
} from "@tanstack/react-table";
import debounce from "lodash.debounce";
import {
    faSearch,
    faThumbsDown,
    faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useAuth from "@/lib/auth/useAuth";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { roundTo } from "@/lib/utils/numbers";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { parseDate } from "@internationalized/date";
import type { DateValue } from "@internationalized/date";
import TableV2 from "@/components/table/TableV2";

export const Route = createFileRoute("/dashboard/home/imagery-qaqc")({
    component: RouteComponent,
});

type ImageryRow = Imagery & { field?: FieldSummary };

function RouteComponent() {
    const { formatDateMMDDYYYY } = useI18n();
    const { t } = useTranslation("messages");
    const { useGetRequest, useMutateRequest } = useRequest();
    const { user } = useAuth();

    const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>({
        start: parseDate(dayjs().subtract(3, "days").format("YYYY-MM-DD")),
        end: parseDate(dayjs().format("YYYY-MM-DD")),
    });

    const [hasBeenReviewed, setHasBeenReviewed] = useState<
        "" | "true" | "false"
    >("");
    const [recentRecs, setRecentRecs] = useState<boolean>(false);
    const [usable, setUsable] = useState<"" | "true" | "false">("");

    const flaggedImagery = useGetRequest<Imagery[]>({
        url: "imagery/review/",
        params: {
            start_date: dateRange?.start?.toString(),
            end_date: dateRange?.end?.toString(),

            ...(hasBeenReviewed !== ""
                ? { has_been_reviewed: hasBeenReviewed }
                : {}),
            ...(usable !== "" ? { usable } : {}),
            ...(recentRecs ? { recommend: recentRecs } : {}),
        },
    });

    const sentinelFields = useGetRequest<FieldSummary[]>({
        url: "fields/sentinel_summary/",
    });

    const [sorting, setSorting] = useState<SortingState>([
        {
            id: "capture_date",
            desc: true,
        },
    ]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [accountManagerFilter, setAccountManagerFilter] = useState<
        string | null
    >(null);
    const [serviceProviderFilter, setServiceProviderFilter] = useState<
        string | null
    >(null);
    const [farmFilter, setFarmFilter] = useState<string | null>(null);

    const data: ImageryRow[] = useMemo(() => {
        if (!flaggedImagery.data || !sentinelFields.data) return [];

        let data = flaggedImagery.data.map((imagery) => {
            const field = sentinelFields.data.find(
                (field) => field.id == imagery.field?.id,
            );
            return {
                ...imagery,
                field,
            };
        });
        if (globalFilter.length > 0) {
            data = data.filter((imagery) => {
                return (
                    imagery.field?.name
                        ?.toLowerCase()
                        .includes(globalFilter.toLowerCase()) ||
                    imagery.field?.grower?.name
                        ?.toLowerCase()
                        .includes(globalFilter.toLowerCase()) ||
                    imagery.field?.service_provider?.name
                        ?.toLowerCase()
                        .includes(globalFilter.toLowerCase()) ||
                    imagery.field?.service_provider?.account_manager?.full_name
                        ?.toLowerCase()
                        .includes(globalFilter.toLowerCase()) ||
                    imagery.field?.service_provider?.account_manager?.full_name
                        ?.toLowerCase()
                        .includes(globalFilter.toLowerCase())
                );
            });
        }
        if (accountManagerFilter) {
            data = data.filter((imagery) => {
                return imagery.field?.service_provider?.account_manager?.full_name
                    ?.toLowerCase()
                    .includes(accountManagerFilter.toLowerCase());
            });
        }
        if (serviceProviderFilter) {
            data = data.filter((imagery) => {
                return imagery.field?.service_provider?.name
                    ?.toLowerCase()
                    .includes(serviceProviderFilter.toLowerCase());
            });
        }
        if (farmFilter) {
            data = data.filter((imagery) => {
                return imagery.field?.grower?.name
                    ?.toLowerCase()
                    .includes(farmFilter.toLowerCase());
            });
        }
        if (sorting.length > 0) {
            return sortArrayByKey(
                data,
                sorting[0].id as keyof ImageryRow,
                sorting[0].desc === true ? "desc" : "asc",
            );
        }
        return data;
    }, [
        flaggedImagery.data,
        sentinelFields.data,
        sorting,
        globalFilter,
        accountManagerFilter,
        serviceProviderFilter,
        farmFilter,
    ]);

    const hasImages = useMemo(() => {
        return data.some((imagery) => {
            if (!imagery.field?.service_provider?.account_manager?.full_name) {
                return false;
            }

            return (
                imagery.field?.service_provider?.account_manager?.full_name ==
                user?.full_name
            );
        });
    }, [data, user?.full_name]);

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const selectedRow = useMemo(() => {
        const selectedIds = Object.keys(rowSelection);
        if (selectedIds.length === 0) return undefined;
        return data.find((row) => String(row.id) === selectedIds[0]);
    }, [rowSelection, data]);

    const imageryQuery = useGetRequest<Imagery>({
        url: `imagery/${selectedRow?.id}/`,
        enabled: !!selectedRow?.id,
    });

    const [globalSearch, setGlobalSearch] = useState("");

    useEffect(() => {
        const debouncedUpdate = debounce(() => {
            setGlobalFilter(globalSearch);
        }, 200);

        debouncedUpdate();

        return () => debouncedUpdate.cancel();
    }, [globalSearch]);

    const updateImagery = useMutateRequest({
        successMessage: t("success.imagery_updated"),
        errorMessage: t("error.failed_to_update_imagery"),
    });

    const partialAnalysis = useMutateRequest({
        method: "post",
        successMessage: "Partial analysis started",
    });

    const columns: ColumnDef<ImageryRow>[] = useMemo(
        () => [
            {
                id: "grower",
                header: "Farm/CSP",
                accessorFn: (row) => row.field?.grower?.name,
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-col pl-3">
                            <div>{row?.original?.field?.grower?.name}</div>
                            <div className="text-xs text-gray-500">
                                {row?.original?.field?.service_provider?.name ??
                                    "N/A"}
                                {" - "}
                                {row?.original?.field?.service_provider
                                    ?.account_manager?.full_name ?? "N/A"}
                            </div>
                        </div>
                    );
                },
                size: 250,
            },
            {
                id: "name",
                header: "Field",
                size: 170,
                accessorFn: (row) => row.field?.name ?? "",
                cell: (info) => {
                    const fieldId = info.row.original.field?.id;
                    return (
                        <div className="flex flex-col text-xs">
                            {fieldId != null ? (
                                <Link
                                    to="/dashboard/fields/$fieldId"
                                    params={{ fieldId: fieldId.toString() }}
                                    target="_blank"
                                    className="text-primary-500 hover:underline hover:cursor-pointer"
                                >
                                    {String(info.getValue() ?? "")}
                                </Link>
                            ) : (
                                <span>{String(info.getValue() ?? "")}</span>
                            )}
                            <span className="text-gray-500">{info.row.id}</span>
                        </div>
                    );
                },
            },
            {
                id: "capture_date",
                header: "Date",
                sortingFn: "datetime",
                maxSize: 90,
                accessorFn: (row) => row.capture_date,
                cell: (row) => {
                    return (
                        <div className="text-xs flex flex-col">
                            <span>
                                {formatDateMMDDYYYY(row.getValue() as string)}
                            </span>
                            <span>
                                {dayjs(row.getValue() as string).format(
                                    "hh:mm a",
                                )}
                            </span>
                        </div>
                    );
                },
            },
            {
                id: "model_classification",
                header: "Model",
                maxSize: 80,
                accessorFn: (row) => [
                    row.model_classification ?? undefined,
                    row.coverage_percent ?? undefined,
                ],
                cell: (row) => {
                    const [modelClassification, coveragePercent] =
                        row.getValue() as [ModelClassification, number];

                    return (
                        <div className="text-xs flex flex-col items-start">
                            <span
                                className={classNames({
                                    "rounded-md text-white p-[0.15rem] font-bold":
                                        true,
                                    "bg-gray-800": !modelClassification,
                                    "bg-red-500":
                                        modelClassification ==
                                        ModelClassification.bad,
                                    "bg-green-500":
                                        modelClassification ==
                                        ModelClassification.good,
                                    "bg-yellow-500":
                                        modelClassification ==
                                        ModelClassification.flag,
                                })}
                            >
                                {getClassificationLabel(modelClassification) ??
                                    "N/A"}
                            </span>
                            {coveragePercent != null && (
                                <span className="text-xs">
                                    Cov:{roundTo(coveragePercent, 0)}%
                                </span>
                            )}
                        </div>
                    );
                },
            },

            {
                id: "provider_name",
                header: "Provider",
                maxSize: 100,
                accessorFn: (row) => row.provider_name ?? "N/A",
                cell: (info) => (
                    <div className="text-xs">{String(info.getValue())}</div>
                ),
            },
            {
                id: "usable",
                header: "Usable",
                maxSize: 70,
                accessorFn: (row) => row.usable,
                cell: (info) => {
                    const val = info.getValue() as boolean | null;
                    const pending = info.row.original.pending;
                    return (
                        <div className="text-xs flex flex-col items-start gap-0.5">
                            <span
                                className={classNames(
                                    "text-xs font-bold rounded-md p-[0.15rem] text-white",
                                    {
                                        "bg-green-500": val === true,
                                        "bg-red-500": val === false,
                                        "bg-gray-400": val == null,
                                    },
                                )}
                            >
                                {val === true
                                    ? "Yes"
                                    : val === false
                                      ? "No"
                                      : "N/A"}
                            </span>
                            {pending && (
                                <span className="text-[10px] leading-none font-semibold rounded px-1 py-px bg-yellow-100 text-yellow-800">
                                    pending review
                                </span>
                            )}
                        </div>
                    );
                },
            },
            {
                id: "si_mean",
                header: "SI",
                maxSize: 130,
                accessorFn: (row) => [row.si_min, row.si_mean],
                cell: (row) => {
                    const [min, mean] = row.getValue() as [number, number];
                    return (
                        <div className="text-xs grid grid-cols-2 gap-y-0.5">
                            <span>Min:</span>
                            <span> {roundTo(min, 4)}</span>
                            <span>Mean:</span>
                            <span> {roundTo(mean, 4)}</span>
                            <span
                                className={classNames(
                                    "col-span-2 w-fit text-[10px] leading-none font-semibold rounded px-1 py-px",
                                    row.row.original.recommend_fertigation
                                        ? "bg-red-100 text-red-700"
                                        : "bg-green-100 text-green-700",
                                )}
                            >
                                {row.row.original.recommend_fertigation
                                    ? "Apply N"
                                    : "Not Apply"}
                            </span>
                        </div>
                    );
                },
            },
            {
                id: "actions",
                header: "Actions",
                accessorFn: () => null,
                maxSize: 140,
                cell: ({ row }) => {
                    return (
                        <div
                            className="flex gap-2"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Button
                                size="sm"
                                isIconOnly
                                isLoading={updateImagery.isPending}
                                color="success"
                                onPress={() => {
                                    updateImagery.mutate(
                                        {
                                            url: `imagery/${row.original.id}/score/`,
                                            data: {
                                                user_classification:
                                                    ModelClassification.good,
                                            },
                                            method: "put",
                                        },
                                        {
                                            onSuccess: () => {
                                                flaggedImagery.refetch();
                                                const nextRow =
                                                    data[
                                                        data.findIndex(
                                                            (r) =>
                                                                r.id ===
                                                                row.original.id,
                                                        ) + 1
                                                    ];
                                                setRowSelection(
                                                    nextRow
                                                        ? {
                                                              [String(
                                                                  nextRow.id,
                                                              )]: true,
                                                          }
                                                        : {},
                                                );
                                            },
                                        },
                                    );
                                }}
                            >
                                <FontAwesomeIcon icon={faCheck} />
                            </Button>
                            <Button
                                color="danger"
                                isIconOnly
                                size="sm"
                                isLoading={updateImagery.isPending}
                                onPress={() => {
                                    updateImagery.mutate(
                                        {
                                            url: `imagery/${row.original.id}/score/`,
                                            data: {
                                                user_classification:
                                                    ModelClassification.bad,
                                            },
                                            method: "put",
                                        },
                                        {
                                            onSuccess: () => {
                                                flaggedImagery.refetch();
                                                const nextRow =
                                                    data[
                                                        data.findIndex(
                                                            (r) =>
                                                                r.id ===
                                                                row.original.id,
                                                        ) + 1
                                                    ];
                                                setRowSelection(
                                                    nextRow
                                                        ? {
                                                              [String(
                                                                  nextRow.id,
                                                              )]: true,
                                                          }
                                                        : {},
                                                );
                                            },
                                        },
                                    );
                                }}
                            >
                                <FontAwesomeIcon icon={faThumbsDown} />
                            </Button>
                            <Button
                                color={
                                    row.original.use_partial_analysis
                                        ? "primary"
                                        : "default"
                                }
                                isLoading={partialAnalysis.isPending}
                                isIconOnly
                                size="sm"
                                onPress={() => {
                                    partialAnalysis.mutate(
                                        {
                                            url: `imagery/${row.original.id}/create_usable_imagery_boundary/`,
                                        },
                                        {
                                            onSuccess: () => {
                                                flaggedImagery.refetch();
                                            },
                                        },
                                    );
                                }}
                            >
                                <FontAwesomeIcon icon={faWandMagicSparkles} />
                            </Button>
                        </div>
                    );
                },
            },
        ],
        [
            formatDateMMDDYYYY,
            updateImagery,
            flaggedImagery,
            data,
            partialAnalysis,
        ],
    );

    useEffect(() => {
        if (hasImages && user?.full_name) {
            const fullName = user.full_name;
            setAccountManagerFilter(fullName);
        }
    }, [hasImages, user?.full_name]);

    const filterOptions = {
        account_manager: Array.from(
            new Set(
                data.map(
                    (row) =>
                        row.field?.service_provider?.account_manager
                            ?.full_name ?? "N/A",
                ),
            ),
        ).map((account_manager) => (
            <AutocompleteItem key={account_manager} textValue={account_manager}>
                {account_manager}
            </AutocompleteItem>
        )),

        service_provider: Array.from(
            new Set(data.map((row) => row.field?.service_provider?.name ?? "")),
        ).map((service_provider) => (
            <AutocompleteItem
                key={service_provider}
                textValue={
                    service_provider != ""
                        ? service_provider
                        : "no service provider"
                }
            >
                {service_provider}
            </AutocompleteItem>
        )),

        farm: Array.from(
            new Set(data.map((row) => row.field?.grower?.name ?? "")),
        ).map((farm) => (
            <AutocompleteItem
                key={farm}
                textValue={farm != "" ? farm : "no farm"}
            >
                {farm}
            </AutocompleteItem>
        )),
    };

    const [showUsableOverlay, setShowUsableOverlay] = useState(false);

    return (
        <div className="grid grid-rows-[1fr_1fr] md:grid-rows-none md:grid-cols-[2fr_1fr] h-full overflow-hidden ">
            <div className="h-full min-h-0 flex flex-col">
                <div className="flex flex-row gap-2 p-2 items-center justify-between">
                    <div>
                        <Input
                            startContent={<FontAwesomeIcon icon={faSearch} />}
                            value={globalSearch}
                            isClearable
                            onClear={() => setGlobalSearch("")}
                            onChange={(e) =>
                                setGlobalSearch(String(e.target.value))
                            }
                            placeholder="Search..."
                        />
                    </div>
                    <div className="flex flex-row gap-2">
                        <ButtonGroup size="sm">
                            <Button
                                color={
                                    hasBeenReviewed === ""
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => setHasBeenReviewed("")}
                            >
                                All
                            </Button>

                            <Button
                                color={
                                    hasBeenReviewed === "false"
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => setHasBeenReviewed("false")}
                            >
                                Unreviewed
                            </Button>
                            <Button
                                color={
                                    hasBeenReviewed === "true"
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => setHasBeenReviewed("true")}
                            >
                                Reviewed
                            </Button>
                        </ButtonGroup>
                        <div>
                            <Button
                                color={recentRecs ? "primary" : "default"}
                                onPress={() => setRecentRecs(!recentRecs)}
                                startContent={
                                    recentRecs ? (
                                        <FontAwesomeIcon icon={faCheck} />
                                    ) : null
                                }
                                size="sm"
                            >
                                Recs Only
                            </Button>
                        </div>

                        <ButtonGroup size="sm">
                            <Button
                                color={usable === "" ? "primary" : "default"}
                                onPress={() => setUsable("")}
                            >
                                All
                            </Button>
                            <Button
                                color={
                                    usable === "true" ? "primary" : "default"
                                }
                                onPress={() => setUsable("true")}
                            >
                                Usable
                            </Button>
                            <Button
                                color={
                                    usable === "false" ? "primary" : "default"
                                }
                                onPress={() => setUsable("false")}
                            >
                                Not usable
                            </Button>
                        </ButtonGroup>

                        <DateRangePicker
                            value={dateRange}
                            onChange={(value) => value && setDateRange(value)}
                            size="sm"
                        />
                    </div>
                </div>

                <div className="flex flex-row gap-2 px-2 pb-2 items-center">
                    <Autocomplete
                        startContent={<FontAwesomeIcon icon={faSearch} />}
                        placeholder="Account Manager"
                        selectedKey={accountManagerFilter}
                        onSelectionChange={(key) =>
                            setAccountManagerFilter(key ? String(key) : null)
                        }
                        size="sm"
                    >
                        {filterOptions.account_manager}
                    </Autocomplete>

                    <Autocomplete
                        startContent={<FontAwesomeIcon icon={faSearch} />}
                        placeholder="Service Provider"
                        selectedKey={serviceProviderFilter}
                        onSelectionChange={(key) =>
                            setServiceProviderFilter(key ? String(key) : null)
                        }
                        size="sm"
                    >
                        {filterOptions.service_provider}
                    </Autocomplete>

                    <Autocomplete
                        startContent={<FontAwesomeIcon icon={faSearch} />}
                        placeholder="Farm"
                        selectedKey={farmFilter}
                        onSelectionChange={(key) =>
                            setFarmFilter(key ? String(key) : null)
                        }
                        size="sm"
                    >
                        {filterOptions.farm}
                    </Autocomplete>
                </div>

                <Divider />

                <div className="flex-1 min-h-0 overflow-y-auto">
                    <TableV2
                        data={data}
                        columns={columns}
                        sorting={sorting}
                        setSorting={setSorting}
                        manualSorting={true}
                        isLoading={
                            flaggedImagery.isLoading || sentinelFields.isLoading
                        }
                        rowSelection={rowSelection}
                        onRowSelectionChange={setRowSelection}
                        enableMultiRowSelection={false}
                        getRowId={(row) => String(row.id)}
                    />
                </div>
            </div>

            <div className="flex flex-row overflow-y-scroll">
                <Divider orientation="vertical" />
                <div className="p-2 flex flex-col gap-2 justify-start items-start  ">
                    <div>Click Imagery To View</div>

                    <div className="flex flex-col gap-2">
                        <div className="flex flex-row gap-2 items-center justify-between">
                            <p className="font-semibold">RGB</p>
                            <div>
                                <Checkbox
                                    isSelected={showUsableOverlay}
                                    onValueChange={setShowUsableOverlay}
                                >
                                    Show Usable Overlay
                                </Checkbox>
                            </div>
                        </div>
                        {imageryQuery.data?.display_urls?.rgb && (
                            <div className="bg-default-200 rounded-md p-2">
                                <div className="relative w-full min-w-[25rem]">
                                    <img
                                        src={imageryQuery.data.display_urls.rgb}
                                        className="w-full h-auto min-h-[25rem] object-contain border-2 border-gray-500 rounded-md scale-x-[-1] rotate-180 block"
                                    />
                                    {imageryQuery.data.display_urls
                                        .usable_overlay &&
                                        showUsableOverlay &&
                                        imageryQuery.data
                                            .use_partial_analysis && (
                                            <img
                                                src={
                                                    imageryQuery.data
                                                        .display_urls
                                                        .usable_overlay
                                                }
                                                alt="Usable overlay"
                                                className="absolute inset-0 w-full h-full object-contain scale-x-[-1] rotate-180 rounded-md pointer-events-none"
                                            />
                                        )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        <p className="font-semibold">NDRE</p>
                        {imageryQuery.data?.display_urls?.ndre && (
                            <div className="bg-default-200 rounded-md p-2">
                                <img
                                    src={imageryQuery.data.display_urls.ndre}
                                    className="w-full min-w-[25rem] h-auto min-h-[25rem] object-contain border-2 border-gray-500 rounded-md scale-x-[-1] rotate-180"
                                />
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        <p className="font-semibold">NDVI</p>
                        {imageryQuery.data?.display_urls?.ndvi && (
                            <div className="bg-default-200 rounded-md p-2">
                                <img
                                    src={imageryQuery.data.display_urls.ndvi}
                                    className="w-full min-w-[25rem] h-auto min-h-[25rem] object-contain border-2 border-gray-500 rounded-md scale-x-[-1] rotate-180"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
